import axiosInstance from '@/lib/enhancedAxiosInstance';
import { analyticsLocalizationQuality } from '@/lib/apiPaths';
import type { ReportFiltersState } from '@/features/reports/components/filters/ReportFilters';
import { createLogger } from '@/lib/logger';

// Create component-specific logger
const localizationLogger = createLogger('LocalizationService');

/**
 * Interface for the response from the localization quality endpoint
 */
export interface LocalizationCoverageResponse {
  overall: {
    total_attributes: number;
    translated_attributes: number;
    translated_pct: number;
  };
  locale_stats: Array<{
    locale: string;
    translated_pct: number;
    total_attributes: number;
    translated_attributes: number;
  }>;
  pagination?: {
    total_pages: number;
    current_page: number;
    total_items: number;
    page_size: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/**
 * Interface for localization coverage data returned from the API
 */
export interface LocalizationCoverageData {
  locale_stats: Array<{
    locale: string;
    translated_pct: number;
    total_attributes: number;
    translated_attributes: number;
  }>;
}

/**
 * Fetches localization quality analytics with filtering support
 *
 * @param filters - Report filters state object including locale, category, etc.
 * @returns Promise resolving to the localization quality data
 */
export async function getLocalizationQuality(
  filters: ReportFiltersState
): Promise<LocalizationCoverageResponse> {
  try {
    const params = {
      from_date: filters.from ? new Date(filters.from).toISOString() : undefined,
      to_date: filters.to ? new Date(filters.to).toISOString() : undefined,
      locale: filters.locale,
      category: filters.category,
      channel: filters.channel,
      family: filters.family,
      family_id: filters.family,
    };

    localizationLogger.info('Fetching localization quality data', { params });

    const { data } = await axiosInstance.get<any>(analyticsLocalizationQuality(), { params });

    // Transform the API response to match our expected interface
    // The API returns an array of localization quality records
    if (Array.isArray(data)) {
      const localeStats = data.map(item => ({
        locale: item.locale_code || item.locale,
        translated_pct: item.translation_coverage || 0,
        total_attributes: item.total_attributes || 0,
        translated_attributes: item.translated_attributes || 0,
      }));

      // Calculate overall statistics
      const totalAttributes = localeStats.reduce((sum, locale) => sum + locale.total_attributes, 0);
      const totalTranslated = localeStats.reduce(
        (sum, locale) => sum + locale.translated_attributes,
        0
      );
      const overallPct = totalAttributes > 0 ? (totalTranslated / totalAttributes) * 100 : 0;

      return {
        overall: {
          total_attributes: totalAttributes,
          translated_attributes: totalTranslated,
          translated_pct: overallPct,
        },
        locale_stats: localeStats,
      };
    }

    // If the API response is already in the expected format, return as-is
    return data as LocalizationCoverageResponse;
  } catch (error) {
    localizationLogger.error(
      'Failed to fetch localization quality data',
      {
        error: (error as Error).message,
        filters,
      },
      error as Error
    );
    throw error;
  }
}

/**
 * Fetches localization coverage data with filtering support
 *
 * @param filters - Report filters state object including locale, category, etc.
 * @returns Promise resolving to the localization coverage data
 */
export async function getLocalizationCoverage(
  filters: ReportFiltersState
): Promise<LocalizationCoverageData> {
  try {
    // Build query string from filters
    const queryParams = new URLSearchParams();

    if (filters.locale) queryParams.append('locale', filters.locale);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.channel) queryParams.append('channel', filters.channel);
    if (filters.family) queryParams.append('family', filters.family);
    if (filters.from) queryParams.append('date_from', filters.from);
    if (filters.to) queryParams.append('date_to', filters.to);

    // Get the base URL
    const baseUrl = analyticsLocalizationQuality();

    // Add query params if any exist
    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

    localizationLogger.info('Fetching localization coverage data', { url });

    const response = await axiosInstance.get<LocalizationCoverageData>(url);
    return response.data;
  } catch (error) {
    localizationLogger.error(
      'Failed to fetch localization coverage data',
      {
        error: (error as Error).message,
        filters,
      },
      error as Error
    );
    throw error;
  }
}

/**
 * Service exports for localization data
 */
const localizationService = {
  getLocalizationCoverage,
};

export default localizationService;
